<template>
  <div :class="isScratchcardEnabled ? 'pt-200' : 'pt-0'">
    <NuxtLayout
      name="teasers"
      v-bind="{ loading }"
      :no-results="!teasers.length"
      @refresh="handleRefetch"
    >
      <component
        :is="getTeaserComponent(teaser)"
        v-for="(teaser, index) in teasers"
        :key="`${index}_${locale}`"
        v-bind="{
          ...teaser,
          licensedTerritory,
        }"
        @click="handleTeaserClick(teaser)"
        @update-teasers="handleRefetch"
      />
    </NuxtLayout>
  </div>
</template>

<script lang="ts" setup>
import type {
  DefaultTeasersPayload,
  FreeToPlayTeaserPayload,
  HighFrequencyTeaserPayload,
  LotteryTeaserPayload,
  ScratchcardTeaserPayload,
} from '#api/shared/@types/generated/backend/graphql-schema-types'

import FreeToPlayTeaser from '#free-to-play/app/components/FreeToPlayTeaser.vue'
import { FREE_TO_PLAY_ITEM_NAME } from '#free-to-play/app/constants/constants'
import HighFrequencyGameTeaser from '#high-frequency-game/app/components/HighFrequencyGameTeaser.vue'
import LotteryTeaser from '#product/app/components/LotteryTeaser.vue'

type PlayNowLotteriesProps = {
  isScratchcardEnabled: boolean
  loading?: boolean
  lotteryTeasers?: DefaultTeasersPayload
}

const { lotteryTeasers } = defineProps<PlayNowLotteriesProps>()

const emit = defineEmits<{
  (e: 'refetch'): void
}>()

const { locale } = useI18n()
const licensedTerritory = useLicensedTerritory({ raw: true })

const { filterFreeToPlayTeasers, isFreeToPlayTeaser } = useFreeToPlay()

const handleRefetch = () => {
  emit('refetch')
}

const TRACKING_LIST_NAME = 'Play Now Lottery Teasers'
const TRACKING_LIST_ID = 'play_now_lottery_teasers'

const { createTeaserListItems } = useTrackingData()
const { pushSelectListItemEvent, pushViewListEvent } = useTracking()
const { logTrackingError } = useTrackingErrorLog()

const trackingTeaserListItems = ref<ReturnType<typeof createTeaserListItems>>([])

const teasers = computed(() => filterFreeToPlayTeasers(lotteryTeasers?.teasers))

const getTeaserComponent = (
  teaser:
    | FreeToPlayTeaserPayload
    | HighFrequencyTeaserPayload
    | LotteryTeaserPayload
    | ScratchcardTeaserPayload,
) => {
  if (teaser.__typename === 'LotteryTeaserPayload') {
    return LotteryTeaser
  }

  if (teaser.__typename === 'HighFrequencyTeaserPayload') {
    return HighFrequencyGameTeaser
  }

  if (isFreeToPlayTeaser(teaser)) {
    return FreeToPlayTeaser
  }

  return null
}

const handleTeaserClick = (
  teaser:
    | FreeToPlayTeaserPayload
    | HighFrequencyTeaserPayload
    | LotteryTeaserPayload
    | ScratchcardTeaserPayload,
) => {
  const teaserItem = trackingTeaserListItems.value.find((item) => {
    switch (teaser.__typename) {
      case 'FreeToPlayTeaserPayload':
        return item.item_name === FREE_TO_PLAY_ITEM_NAME
      case 'HighFrequencyTeaserPayload':
      case 'LotteryTeaserPayload':
        return item.item_id === teaser.id
      default:
        return false
    }
  })

  if (!teaserItem) return

  try {
    pushSelectListItemEvent({
      items: [teaserItem],
      list_id: TRACKING_LIST_ID,
      list_name: TRACKING_LIST_NAME,
    })
  } catch (err) {
    logTrackingError('select_list_item', err)
  }
}

let unwatchTeasers: Maybe<() => void> = null
unwatchTeasers = watchImmediate(teasers, (list) => {
  if (!list || list.length === 0) return

  try {
    trackingTeaserListItems.value = createTeaserListItems(list)
    pushViewListEvent({
      items: trackingTeaserListItems.value,
      list_id: TRACKING_LIST_ID,
      list_name: TRACKING_LIST_NAME,
    })

    if (unwatchTeasers) unwatchTeasers()
  } catch (err) {
    logTrackingError('view_list', err)
  }
})
</script>
